import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MultiBuilderFiltersModal from './MultiBuilderFiltersModal';
import {
    BookmakerLogo,
    BOOKMAKER_DISPLAY_NAMES,
    MetricBadge,
    formatOdds,
    formatStartTime,
    shortenMatchName,
    getLegKey,
    AFL_PROP_TYPE_OPTIONS,
    AFL_SEASON_OPTIONS,
    AFL_SEASON_SHORT_LABEL,
    getAflPropTypeFromMarket,
    getImpliedFields,
    isImpliedValid,
    KNOWN_BOOKMAKERS,
} from './multiBuilderHelpers';
import SubscriptionRequired from '../common/SubscriptionRequired';

const DATA_SOURCE_OPTIONS = [
    { value: 'snipe_feed', label: 'Snipe Feed' },
];

function CountdownRing({ countdown, total = 10 }) {
    const radius = 7;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (countdown / total) * circumference;
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" className="-rotate-90">
            <circle
                cx="10"
                cy="10"
                r={radius}
                stroke="rgba(148,163,184,0.18)"
                strokeWidth="1.5"
                fill="none"
            />
            <circle
                cx="10"
                cy="10"
                r={radius}
                stroke="rgb(52,211,153)"
                strokeWidth="1.5"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
        </svg>
    );
}

/**
 * Minimal info-icon with a hover-revealed tooltip. Same UX as Snipe Feed's
 * `InfoTooltip` — inlined here to avoid creating a cross-component dependency
 * just for one icon.
 */
function InfoTooltip({ title, children, align = 'left' }) {
    const alignCls = align === 'right'
        ? 'right-0'
        : align === 'center'
            ? 'left-1/2 -translate-x-1/2'
            : 'left-0';
    return (
        <span
            tabIndex={0}
            role="button"
            aria-label={`${title}: more info`}
            className="relative group inline-flex items-center cursor-help text-slate-500 hover:text-slate-300 focus:text-slate-300 focus:outline-none transition-colors"
        >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 16v-4M12 8h.01" />
            </svg>
            <span
                role="tooltip"
                className={`pointer-events-none absolute top-full mt-1.5 ${alignCls} z-[100]
                    w-64 max-w-[calc(100vw-1rem)] px-3 py-2.5 rounded-lg
                    bg-slate-900/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40
                    whitespace-normal break-words normal-case tracking-normal
                    opacity-0 translate-y-1
                    group-hover:opacity-100 group-hover:translate-y-0
                    group-focus:opacity-100 group-focus:translate-y-0
                    transition-all duration-150`}
            >
                {title && (
                    <span className="block text-[10px] font-mono font-semibold uppercase tracking-widest text-emerald-400 mb-1.5">
                        {title}
                    </span>
                )}
                <span className="block text-[11px] font-mono font-normal text-slate-300 leading-relaxed">
                    {children}
                </span>
            </span>
        </span>
    );
}

/**
 * Sortable column-header cell. Div-based (rather than <th>) because the surrounding
 * layout uses two separate scroll regions for header and body — see notes near
 * `MultiBuilderTable` — so the header isn't really a table thead anymore.
 *
 * When `sticky` is true the cell pins itself to the left edge of its (horizontal)
 * scroll container via `position: sticky` + the supplied `left` offset.
 */
function SortableHeader({
    sortKey,
    label,
    sortConfig,
    onSort,
    accent,
    sticky = false,
    left = 0,
    width,
    isLast = false,
    align = 'left',
}) {
    const isActive = sortConfig.key === sortKey;
    const dir = isActive ? sortConfig.direction : null;
    const colorCls = accent === 'emerald' ? 'text-emerald-400' : 'text-slate-500';
    const activeCls = isActive ? 'text-white' : '';
    const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
    const stickyCls = sticky
        ? `sticky z-10 bg-slate-800${isLast ? ' shadow-[inset_-1px_0_0_rgba(71,85,105,0.5)]' : ''}`
        : 'bg-slate-800';
    return (
        <button
            type="button"
            role="columnheader"
            aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            onClick={() => onSort(sortKey)}
            className={`flex items-center ${justify} px-3 py-3 whitespace-nowrap text-[10px] font-mono font-semibold uppercase tracking-widest cursor-pointer select-none transition-colors ${colorCls} hover:text-emerald-300 ${stickyCls} flex-shrink-0 border-b border-slate-700/50`}
            style={{ width, minWidth: width, maxWidth: width, left: sticky ? left : undefined }}
        >
            <span className={`inline-flex items-center gap-1 ${activeCls}`}>
                <span>{label}</span>
                <svg
                    className={`w-2.5 h-2.5 transition-opacity ${isActive ? 'opacity-100' : 'opacity-30'}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    {dir === 'asc' ? (
                        <path d="M12 19V5M5 12l7-7 7 7" />
                    ) : (
                        <path d="M12 5v14M5 12l7 7 7-7" />
                    )}
                </svg>
            </span>
        </button>
    );
}

function Header({
    dataSource,
    setDataSource,
    aflPropType,
    setAflPropType,
    searchTerm,
    setSearchTerm,
    aflSeasonView,
    setAflSeasonView,
    selectedGame,
    setSelectedGame,
    availableGames,
    countdown,
    onRefresh,
    isLoading,
    matchedCount,
    totalCount,
    onOpenFilters,
    activeFilterCount,
    sortConfig,
    setSortConfig,
}) {
    // Mobile: the dropdowns / search / game selector live inside a collapsible
    // panel toggled by the gear icon. Keeps the always-visible row compact and
    // mirrors the Snipe Feed mobile header pattern.
    const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
    return (
        <div className="border-b border-slate-700/50">
            {/* Desktop / wide */}
            <div className="hidden md:flex items-center justify-between px-3 py-2.5 gap-3">
                <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
                    {/* Data source */}
                    <label className="flex items-center gap-1.5">
                        <span className="text-[9px] font-mono text-slate-600 uppercase tracking-widest">Source</span>
                        <select
                            value={dataSource}
                            onChange={(e) => setDataSource(e.target.value)}
                            className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-[11px] font-mono text-emerald-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                        >
                            {DATA_SOURCE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </label>
                    {/* Prop type */}
                    <label className="flex items-center gap-1.5">
                        <span className="text-[9px] font-mono text-slate-600 uppercase tracking-widest">Prop</span>
                        <select
                            value={aflPropType}
                            onChange={(e) => setAflPropType(e.target.value)}
                            aria-label="Prop type"
                            className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-[11px] font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                        >
                            {AFL_PROP_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </label>
                    {/* Dataset (replaces sort dropdown) */}
                    <label className="flex items-center gap-1.5">
                        <span className="text-[9px] font-mono text-slate-600 uppercase tracking-widest inline-flex items-center gap-1">
                            Dataset
                            <InfoTooltip title="Dataset">
                                Switches the historical sample used to calculate the
                                <span className="text-white"> Implied Odds </span>
                                and
                                <span className="text-white"> Implied % </span>
                                columns.
                                <span className="block mt-2 space-y-0.5 text-slate-400">
                                    <span className="block">
                                        <span className="text-slate-200">2026</span> — current season only.
                                    </span>
                                    <span className="block">
                                        <span className="text-slate-200">2025 + 2026</span> — combined for a larger sample.
                                    </span>
                                </span>
                            </InfoTooltip>
                        </span>
                        <select
                            value={aflSeasonView}
                            onChange={(e) => setAflSeasonView(e.target.value)}
                            aria-label="Dataset"
                            className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-[11px] font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                        >
                            {AFL_SEASON_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </label>
                    {/* Game */}
                    {availableGames.length > 0 && (
                        <select
                            value={selectedGame}
                            onChange={(e) => setSelectedGame(e.target.value)}
                            aria-label="Game"
                            className={`px-2 py-1 border rounded text-[11px] font-mono focus:outline-none focus:border-emerald-500 cursor-pointer transition-colors max-w-[180px] truncate ${
                                selectedGame === 'all'
                                    ? 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                                    : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
                            }`}
                        >
                            <option value="all">All games</option>
                            {availableGames.map((g) => (
                                <option key={g} value={g}>{shortenMatchName(g, 'afl')}</option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Right cluster */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="relative">
                        <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search player..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-7 pr-2 py-1 bg-slate-800 border border-slate-700 rounded text-[11px] font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-36 hover:border-slate-600 transition-colors"
                        />
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-800/50 rounded">
                        <span className="text-[10px] font-mono font-semibold text-white tabular-nums">{matchedCount}</span>
                        {matchedCount !== totalCount && (
                            <span className="text-[10px] font-mono text-slate-600">/ {totalCount}</span>
                        )}
                        <span className="text-[10px] font-mono text-slate-600">legs</span>
                    </div>
                    <button
                        type="button"
                        onClick={onOpenFilters}
                        aria-label="Open filters"
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded border transition-colors ${
                            activeFilterCount > 0
                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
                        }`}
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                        </svg>
                        <span>Filters</span>
                        {activeFilterCount > 0 && (
                            <span className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-400 text-emerald-950 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                    <CountdownRing countdown={countdown} total={10} />
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={isLoading}
                        aria-label="Refresh"
                        title="Refresh"
                        className="p-1.5 border border-slate-700 rounded text-slate-400 hover:text-emerald-400 hover:border-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <svg className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile — compact action bar (always visible) + collapsible settings. */}
            <div className="md:hidden flex items-center justify-between px-3 py-2.5 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <h3 className="text-sm font-semibold text-white whitespace-nowrap">Multi Builder</h3>
                    <span className="text-[11px] font-mono text-slate-600 tabular-nums">
                        <span className="text-slate-300 font-semibold">{matchedCount}</span>
                        {matchedCount !== totalCount && (
                            <span className="text-slate-600"> / {totalCount}</span>
                        )}
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {!isLoading && <CountdownRing countdown={countdown} total={10} />}
                    <button
                        type="button"
                        onClick={onOpenFilters}
                        aria-label="Open filters"
                        className={`relative p-1.5 border rounded transition-colors ${
                            activeFilterCount > 0
                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                : 'border-slate-700 text-slate-500 hover:text-white hover:border-slate-600'
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                        </svg>
                        {activeFilterCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-1 rounded-full bg-emerald-400 text-emerald-950 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={isLoading}
                        aria-label="Refresh"
                        title="Refresh"
                        className="p-1.5 border border-slate-700 rounded text-slate-400 hover:text-emerald-400 hover:border-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={() => setMobileSettingsOpen((prev) => !prev)}
                        aria-label="Toggle controls"
                        aria-expanded={mobileSettingsOpen}
                        className="p-1.5 border border-slate-700 rounded text-slate-500 hover:text-white hover:border-slate-600 transition-colors"
                    >
                        <svg className={`w-4 h-4 transition-transform duration-200 ${mobileSettingsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                    </button>
                </div>
            </div>

            {mobileSettingsOpen && (
                <div className="md:hidden px-3 pb-3 border-t border-slate-700/50 pt-3 animate-fade-in space-y-2.5">
                    {/* Selects grid. Source has only one option today so we
                        hide it on mobile to save a row — when more sources are
                        added it can be re-introduced. */}
                    <div className="grid grid-cols-2 gap-2 items-end">
                        <label className="flex flex-col gap-1 min-w-0">
                            <span className="text-[9px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                                Prop Type
                            </span>
                            <select
                                value={aflPropType}
                                onChange={(e) => setAflPropType(e.target.value)}
                                aria-label="Prop type"
                                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                            >
                                {AFL_PROP_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 min-w-0">
                            <span className="inline-flex items-center gap-1 text-[9px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                                Dataset
                                <InfoTooltip title="Dataset" align="right">
                                    Switches the historical sample used to calculate the
                                    <span className="text-white"> Implied Odds </span>
                                    and
                                    <span className="text-white"> Implied % </span>
                                    columns.
                                    <span className="block mt-2 space-y-0.5 text-slate-400">
                                        <span className="block">
                                            <span className="text-slate-200">2026</span> — current season only.
                                        </span>
                                        <span className="block">
                                            <span className="text-slate-200">2025 + 2026</span> — combined for a larger sample.
                                        </span>
                                    </span>
                                </InfoTooltip>
                            </span>
                            <select
                                value={aflSeasonView}
                                onChange={(e) => setAflSeasonView(e.target.value)}
                                aria-label="Dataset"
                                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                            >
                                {AFL_SEASON_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {/* Sort + Game filter — 50/50 when both render, sort full-width
                        when no games are available. The desktop relies on tappable
                        column headers; mobile cards don't have that affordance so
                        we surface an explicit Sort By dropdown here. */}
                    <div className={`grid gap-2 items-end ${availableGames.length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        <label className="flex flex-col gap-1 min-w-0">
                            <span className="text-[9px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                                Sort By
                            </span>
                            <select
                                value={sortConfig?.key || 'edge'}
                                onChange={(e) => {
                                    const opt = MOBILE_SORT_OPTIONS.find((o) => o.value === e.target.value);
                                    if (opt) setSortConfig({ key: opt.key, direction: opt.direction });
                                }}
                                aria-label="Sort by"
                                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                            >
                                {MOBILE_SORT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </label>
                        {availableGames.length > 0 && (
                            <label className="flex flex-col gap-1 min-w-0">
                                <span className="text-[9px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                                    Game
                                </span>
                                <select
                                    value={selectedGame}
                                    onChange={(e) => setSelectedGame(e.target.value)}
                                    aria-label="Filter by game"
                                    className={`px-3 py-2 border rounded text-xs font-mono focus:outline-none focus:border-emerald-500 ${
                                        selectedGame === 'all'
                                            ? 'bg-slate-800 border-slate-700 text-white'
                                            : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
                                    }`}
                                >
                                    <option value="all">All games</option>
                                    {availableGames.map((g) => (
                                        <option key={g} value={g}>{shortenMatchName(g, 'afl')}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>

                    <div className="relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Player name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function ErrorState({ error, onRetry }) {
    if (!error) return null;
    if (error.isSubscriptionError) {
        return <SubscriptionRequired feature="Multi Builder" variant="page" />;
    }
    return (
        <div className="flex items-center justify-center py-16 px-6">
            <div className="flex flex-col items-center text-center max-w-sm">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                </div>
                <h4 className="text-sm font-semibold font-mono text-white mb-1">{error.title || 'Unable to load data'}</h4>
                <p className="text-xs font-mono text-slate-500 leading-relaxed mb-4">{error.message}</p>
                {onRetry && (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="px-3 py-1.5 text-xs font-mono font-medium text-emerald-400 border border-emerald-500/30 rounded hover:bg-emerald-500/10 transition-all"
                    >
                        Retry
                    </button>
                )}
            </div>
        </div>
    );
}

// Mobile sort options — the desktop table sorts by clicking column headers,
// which is awkward on a thumb-driven card list, so the mobile collapsible
// surfaces an explicit dropdown. Each option maps to a (key, direction) tuple
// that drives the existing `sortConfig` state.
const MOBILE_SORT_OPTIONS = [
    { value: 'edge', label: 'Best Edge', key: 'edge', direction: 'desc' },
    { value: 'impliedEv', label: 'Best Implied', key: 'impliedEv', direction: 'desc' },
];

const SORT_DEFAULTS = {
    edge: 'desc',
    impliedEv: 'desc',
    bestOdds: 'desc',
    avgOdds: 'desc',
    impliedOdds: 'desc',
    player: 'asc',
    market: 'asc',
};

// All column widths are fixed (px) — the rendered layout is divs/flex, not a <table>,
// so we explicitly size each column to keep header / body alignment + stable sticky
// `left` offsets.
const COL_WIDTHS = {
    checkbox: 36,
    edge: 96,
    implied: 96,
    bestBook: 124,
    player: 200,
    market: 150,
    avgPrice: 90,
    impliedOdds: 130,
    bookmaker: 56,
};
const STICKY_LEFTS = {
    checkbox: 0,
    edge: COL_WIDTHS.checkbox,
    implied: COL_WIDTHS.checkbox + COL_WIDTHS.edge,
    bestBook: COL_WIDTHS.checkbox + COL_WIDTHS.edge + COL_WIDTHS.implied,
    player: COL_WIDTHS.checkbox + COL_WIDTHS.edge + COL_WIDTHS.implied + COL_WIDTHS.bestBook,
    market: COL_WIDTHS.checkbox + COL_WIDTHS.edge + COL_WIDTHS.implied + COL_WIDTHS.bestBook + COL_WIDTHS.player,
};

function stickyCellClasses(selected, isLast = false) {
    const bg = selected
        ? 'bg-[#081b27]'
        : 'bg-[#080f1d] group-hover:bg-slate-800';
    const divider = isLast ? 'shadow-[inset_-1px_0_0_rgba(71,85,105,0.35)]' : '';
    return `sticky z-10 ${bg} ${divider}`;
}

/**
 * Mobile leg card — replaces the wide horizontal table on mobile. Each row in
 * the rendered desktop table becomes one of these cards on small screens. The
 * card is the entire interactive surface; tapping anywhere toggles the leg.
 *
 * Layout in three vertical zones (top → bottom):
 *   1. Identity zone   — checkbox + player/market/match + Edge% badge.
 *   2. Mini-metrics    — three quick-read tiles (Best · Avg · Implied) so the
 *                        most important numbers stay above the fold.
 *   3. Bookmaker strip — every priced bookmaker, horizontally scrollable; the
 *                        best book is emerald-tinted. Lets users power-compare
 *                        without leaving the card or scrolling the page.
 */
// Prominent amber info badge that reveals the standard slate tooltip card on hover
// or keyboard focus, flagging a bookmaker price that is derived (calculated) rather
// than genuinely offered. CSS-only; uses a named group so sibling badges/rows don't
// trigger each other's tooltips.
function DerivedBadge({ size = 13, align = 'center' }) {
    const alignCls = align === 'right'
        ? 'right-0'
        : align === 'left'
            ? 'left-0'
            : 'left-1/2 -translate-x-1/2';
    return (
        <span
            tabIndex={0}
            role="button"
            aria-label="Derived price: more info"
            className="relative group/derived inline-flex items-center align-middle cursor-help text-amber-400 hover:text-amber-300 focus:text-amber-300 focus:outline-none transition-colors"
        >
            <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
            </svg>
            <span
                role="tooltip"
                className={`pointer-events-none absolute bottom-full mb-1.5 ${alignCls} z-[100]
                    w-64 max-w-[calc(100vw-1rem)] px-3 py-2.5 rounded-lg
                    bg-slate-900/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40
                    whitespace-normal break-words normal-case tracking-normal
                    opacity-0 translate-y-1
                    group-hover/derived:opacity-100 group-hover/derived:translate-y-0
                    group-focus/derived:opacity-100 group-focus/derived:translate-y-0
                    transition-all duration-150`}
            >
                <span className="block text-[10px] font-mono font-semibold uppercase tracking-widest text-amber-400 mb-1.5">
                    Derived Price
                </span>
                <span className="block text-[11px] font-mono font-normal text-slate-300 leading-relaxed">
                    Calculated using the odds currently offered by this bookmaker — not a directly offered price.
                </span>
            </span>
        </span>
    );
}

/**
 * Inline "Open Prop Tool" trigger placed immediately before a player's name.
 * Mirrors the Snipe Feed layout: a small square sky search-icon button with the
 * app's standard hover/focus tooltip. Rendered as a `span[role=button]` (rather
 * than a real `<button>`) so it can live inside the mobile leg card, which is
 * itself a `<button>`, without producing nested-button markup. The click is
 * stopped from propagating so it never toggles the row/leg.
 *
 * The tooltip is portaled to `document.body` with fixed positioning rather than
 * absolutely positioned inside the row — the table body is an overflow scroll
 * container, which would otherwise clip the tooltip.
 */
function PropToolButton({ player, onClick }) {
    const ref = useRef(null);
    const [tip, setTip] = useState(null);

    const show = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const maxWidth = 240;
        // Left-align under the trigger, clamped so it never spills off-screen.
        const left = Math.max(8, Math.min(r.left, window.innerWidth - maxWidth - 8));
        // Flip above the trigger if there isn't room below.
        const below = r.bottom + 6;
        const flipUp = below + 36 > window.innerHeight;
        setTip({ left, top: flipUp ? r.top - 6 : below, flipUp, maxWidth });
    }, []);
    const hide = useCallback(() => setTip(null), []);

    const activate = (e) => { e.stopPropagation(); hide(); onClick(); };

    return (
        <span ref={ref} className="inline-flex flex-shrink-0">
            <span
                role="button"
                tabIndex={0}
                onClick={activate}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(e); } }}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                aria-label={`Open Prop Tool - ${player}`}
                className="inline-flex items-center justify-center w-5 h-5 rounded text-sky-400 border border-sky-500/30 hover:bg-sky-500/10 hover:border-sky-500/50 hover:text-sky-300 focus:outline-none cursor-pointer transition-all duration-150"
            >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </span>
            {tip && createPortal(
                <span
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        left: tip.left,
                        top: tip.top,
                        maxWidth: tip.maxWidth,
                        transform: tip.flipUp ? 'translateY(-100%)' : 'none',
                        zIndex: 9999,
                    }}
                    className="pointer-events-none px-2.5 py-1.5 rounded-lg
                        bg-slate-900/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40
                        whitespace-nowrap normal-case tracking-normal animate-fade-in"
                >
                    <span className="block text-[11px] font-mono font-normal text-slate-200">
                        Open Prop Tool <span className="text-slate-500">-</span> {player}
                    </span>
                </span>,
                document.body
            )}
        </span>
    );
}

function MobileLegCard({
    bet,
    selected,
    bestBk,
    impliedFields,
    impliedValid,
    seasonShort,
    bkOddsMap,
    allBookmakers,
    onToggle,
    onOpenPropTool,
}) {
    const opp = bet.valueOpportunities?.[0];
    if (!opp) return null;

    return (
        <button
            type="button"
            onClick={() => onToggle(bet)}
            aria-pressed={selected}
            className={`group relative w-full text-left rounded-lg border transition-all overflow-hidden ${
                selected
                    ? 'border-emerald-500/40 bg-emerald-500/[0.06] shadow-[0_0_0_1px_rgba(16,185,129,0.18)]'
                    : 'border-slate-700/40 bg-slate-900/40 hover:border-slate-600 active:bg-slate-800/30'
            }`}
        >
            {/* Left rail — emerald when selected, slate otherwise, mirrors LegRow in the panel. */}
            <span
                aria-hidden="true"
                className={`absolute left-0 top-2 bottom-2 w-[2px] rounded-r transition-colors ${
                    selected ? 'bg-emerald-400' : 'bg-slate-700/40 group-hover:bg-slate-600/60'
                }`}
            />

            {/* Zone 1 — Identity */}
            <div className="flex items-start gap-2.5 pl-3 pr-2.5 pt-2.5">
                <span
                    aria-hidden="true"
                    className={`mt-0.5 w-5 h-5 inline-flex items-center justify-center rounded border flex-shrink-0 transition-all ${
                        selected
                            ? 'bg-emerald-500/30 border-emerald-400/60'
                            : 'bg-slate-900 border-slate-600'
                    }`}
                >
                    {selected && (
                        <svg className="w-3 h-3 text-emerald-300" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="flex items-center gap-1.5 min-w-0">
                            {onOpenPropTool && (
                                <PropToolButton player={bet.player} onClick={() => onOpenPropTool(bet)} />
                            )}
                            <span className="text-sm font-semibold text-white truncate">
                                {bet.player}
                            </span>
                        </span>
                        <MetricBadge
                            value={opp.impliedEdgePercentage ?? null}
                            formatted={opp.impliedEdgePercentage != null ? `+${opp.impliedEdgePercentage.toFixed(1)}%` : null}
                        />
                    </div>
                    <div className="text-[11px] font-mono text-slate-400 truncate mt-0.5" title={bet.market}>
                        {bet.market}
                    </div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider truncate mt-0.5">
                        {shortenMatchName(bet.match, 'afl')}
                        <span className="text-slate-700"> · </span>
                        <span className="text-slate-600">{formatStartTime(bet.startTime)}</span>
                    </div>
                </div>
            </div>

            {/* Zone 2 — Mini-metrics */}
            <div className="grid grid-cols-3 gap-1.5 px-2.5 pt-2.5">
                <div className="px-2 py-1.5 bg-slate-950/40 border border-slate-800/60 rounded">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-slate-600 leading-none">Best Odds</div>
                    <div className="flex items-center gap-1 mt-1.5">
                        <BookmakerLogo bookmaker={bestBk} size={12} />
                        <span className="text-[12px] font-mono font-bold tabular-nums text-emerald-300 leading-none">
                            {formatOdds(opp.odds)}
                        </span>
                    </div>
                </div>
                <div className="px-2 py-1.5 bg-slate-950/40 border border-slate-800/60 rounded">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-slate-600 leading-none">Avg Price</div>
                    <div className="text-[12px] font-mono tabular-nums text-slate-300 mt-1.5 leading-none">
                        {formatOdds(opp.avgMarketOdds)}
                    </div>
                </div>
                <div className="px-2 py-1.5 bg-slate-950/40 border border-slate-800/60 rounded">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-slate-600 leading-none">
                        Implied Odds ({seasonShort})
                    </div>
                    <div className="text-[12px] font-mono tabular-nums text-slate-300 mt-1.5 leading-none">
                        {impliedValid ? formatOdds(impliedFields.impliedOdds) : '—'}
                    </div>
                </div>
            </div>

            {/* Hit-rate + Implied EV strip (only when implied is valid) */}
            {impliedValid && (
                <div className="flex items-center justify-between px-3 pt-2 text-[10px] font-mono">
                    <span className="text-slate-500 tabular-nums">
                        {impliedFields.lineHit != null && impliedFields.totalGames != null && impliedFields.hitRatePercentage != null
                            ? `${impliedFields.lineHit}/${impliedFields.totalGames} (${parseFloat(Number(impliedFields.hitRatePercentage).toFixed(2))}%)`
                            : ''}
                    </span>
                    {impliedFields.impliedEv != null && (
                        <span className={`tabular-nums font-semibold ${impliedFields.impliedEv >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {impliedFields.impliedEv >= 0 ? '+' : ''}{Number(impliedFields.impliedEv).toFixed(1)}% implied edge
                        </span>
                    )}
                </div>
            )}

            {/* Zone 3 — Bookmaker price strip (horizontal scroll) */}
            <div className="mt-2 pb-2.5 px-2.5">
                <div
                    className="flex items-center gap-1 overflow-x-auto"
                    style={{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {allBookmakers.map((bk) => {
                        const bo = bkOddsMap[bk];
                        if (!bo) return null;
                        const isBest = bk === bestBk;
                        return (
                            <div
                                key={bk}
                                className={`flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-1 rounded border ${
                                    isBest
                                        ? 'border-emerald-500/40 bg-emerald-500/[0.08]'
                                        : 'border-slate-800/60 bg-slate-950/30'
                                }`}
                            >
                                <BookmakerLogo bookmaker={bk} size={11} />
                                <span
                                    className={`text-[11px] font-mono tabular-nums ${
                                        isBest ? 'text-emerald-300 font-semibold' : 'text-slate-400'
                                    }`}
                                >
                                    {formatOdds(bo.odds)}
                                </span>
                                {bo.isDerived && <DerivedBadge size={12} />}
                            </div>
                        );
                    })}
                </div>
            </div>
        </button>
    );
}

function MultiBuilderTable({
    bets,
    isLoading,
    error,
    lastUpdated,
    countdown,
    onRefresh,
    aflSeasonView,
    setAflSeasonView,
    selectedLegs,
    bookmakerFilter,
    onToggleLeg,
    filters,
    onApplyFilters,
    bookmakerLogos,
    bookmakerNames,
    canUsePropTool = false,
    onOpenPropTool,
}) {
    const [dataSource, setDataSource] = useState('snipe_feed');
    const [aflPropType, setAflPropType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGame, setSelectedGame] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'impliedEv', direction: 'desc' });
    const [showFiltersModal, setShowFiltersModal] = useState(false);

    // Kicks/Handballs have far thinner bookmaker coverage (often derived), so drop the
    // minimum-bookmakers filter to 2 for those props and restore the default of 4 for
    // any other prop type (including "All props").
    useEffect(() => {
        if (typeof onApplyFilters !== 'function') return;
        const target = (aflPropType === 'kicks' || aflPropType === 'handballs') ? 2 : 4;
        onApplyFilters((prev) => {
            if (!prev || prev.minBookmakers?.value === target) return prev;
            return { ...prev, minBookmakers: { ...(prev.minBookmakers || { enabled: true }), value: target } };
        });
    }, [aflPropType, onApplyFilters]);

    // Number of currently-applied (enabled, non-empty) filter sections.
    const activeFilterCount = useMemo(() => {
        if (!filters) return 0;
        return Object.entries(filters).filter(([, f]) => {
            if (!f?.enabled) return false;
            if (Array.isArray(f.value)) return f.value.length > 0;
            return true;
        }).length;
    }, [filters]);

    const selectedLegKeys = useMemo(
        () => new Set(selectedLegs.map((l) => l.key)),
        [selectedLegs]
    );

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: SORT_DEFAULTS[key] || 'desc' };
        });
    };

    // All bookmakers represented in any bet — sorted by known ordering, then alpha.
    const allBookmakers = useMemo(() => {
        if (!Array.isArray(bets)) return [];
        const set = new Set();
        bets.forEach((bet) => {
            bet.bookmakerOdds?.forEach((bo) => bo?.bookmaker && set.add(bo.bookmaker));
        });
        const arr = Array.from(set);
        const knownIdx = (bk) => {
            const i = KNOWN_BOOKMAKERS.indexOf(bk);
            return i === -1 ? Number.MAX_SAFE_INTEGER : i;
        };
        arr.sort((a, b) => {
            const ai = knownIdx(a);
            const bi = knownIdx(b);
            if (ai !== bi) return ai - bi;
            return a.localeCompare(b);
        });
        return arr;
    }, [bets]);

    const availableGames = useMemo(() => {
        if (!Array.isArray(bets)) return [];
        const set = new Set();
        bets.forEach((b) => b.match && set.add(b.match));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [bets]);

    // Derive filtered + sorted rows for display
    const rows = useMemo(() => {
        if (!Array.isArray(bets)) return [];
        let data = bets;

        if (aflPropType !== 'all') {
            data = data.filter((b) => getAflPropTypeFromMarket(b.market) === aflPropType);
        }
        if (selectedGame !== 'all') {
            data = data.filter((b) => b.match === selectedGame);
        }
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            data = data.filter((b) => (b.player || '').toLowerCase().includes(term));
        }
        // Bookmaker filter: only legs where the selected bookmaker is the best book.
        if (bookmakerFilter && bookmakerFilter !== 'all') {
            data = data.filter((b) => b?.valueOpportunities?.[0]?.bookmaker === bookmakerFilter);
        }

        // Modal filters — mirrors Snipe Feed's AFL market_and_implied_ev filter pipeline,
        // with `oddsRange` replacing Snipe Feed's single `minOdds` field.
        if (filters?.minBookmakers?.enabled) {
            data = data.filter((b) => (b.bookmakerOdds?.length || 0) >= filters.minBookmakers.value);
        }
        if (filters?.minEv?.enabled) {
            data = data.filter((b) => {
                const edge = b.valueOpportunities?.[0]?.impliedEdgePercentage ?? -Infinity;
                return Math.round(edge * 10) / 10 >= filters.minEv.value;
            });
        }
        if (filters?.minImpliedEv?.enabled) {
            data = data.filter((b) => {
                const opp = b.valueOpportunities?.[0];
                const fields = getImpliedFields(opp, aflSeasonView, opp?.odds);
                const ev = fields.impliedEv ?? -Infinity;
                return ev >= filters.minImpliedEv.value;
            });
        }
        if (filters?.minSampleSize?.enabled) {
            data = data.filter((b) => {
                const opp = b.valueOpportunities?.[0];
                const { totalGames } = getImpliedFields(opp, aflSeasonView, opp?.odds);
                return (totalGames ?? 0) >= filters.minSampleSize.value;
            });
        }
        if (filters?.oddsRange?.enabled) {
            const minN = parseFloat(filters.oddsRange.value?.min);
            const maxN = parseFloat(filters.oddsRange.value?.max);
            const lo = isNaN(minN) ? -Infinity : minN;
            const hi = isNaN(maxN) ? Infinity : maxN;
            data = data.filter((b) => {
                const odds = b.valueOpportunities?.[0]?.odds;
                if (odds == null) return false;
                return odds >= lo && odds <= hi;
            });
        }
        if (filters?.startWithin?.enabled) {
            const cutoff = Date.now() + filters.startWithin.value * 60 * 60 * 1000;
            data = data.filter((b) => {
                if (!b.startTime) return false;
                const startMs = new Date(b.startTime).getTime();
                return startMs <= cutoff;
            });
        }
        if (filters?.excludeBestBookmakers?.enabled && filters.excludeBestBookmakers.value?.length > 0) {
            data = data.filter((b) => {
                const bestBk = b.valueOpportunities?.[0]?.bookmaker;
                return !filters.excludeBestBookmakers.value.includes(bestBk);
            });
        }

        // Dedupe by leg key (defensive — should already be unique from the API)
        const seen = new Set();
        data = data.filter((b) => {
            const key = getLegKey(b);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        const sorted = [...data];
        const { key, direction } = sortConfig;
        const dirMult = direction === 'asc' ? 1 : -1;
        const valueFor = (bet) => {
            const opp = bet?.valueOpportunities?.[0];
            switch (key) {
                case 'edge':
                    return Number(opp?.impliedEdgePercentage ?? -Infinity);
                case 'impliedEv': {
                    const f = getImpliedFields(opp, aflSeasonView, opp?.odds);
                    return f.impliedEv ?? -Infinity;
                }
                case 'bestOdds':
                    return Number(opp?.odds ?? -Infinity);
                case 'avgOdds':
                    return Number(opp?.avgMarketOdds ?? -Infinity);
                case 'impliedOdds': {
                    const f = getImpliedFields(opp, aflSeasonView, opp?.odds);
                    return f.impliedOdds ?? Infinity;
                }
                case 'player':
                    return (bet?.player || '').toLowerCase();
                case 'market':
                    return (bet?.market || '').toLowerCase();
                default:
                    return 0;
            }
        };
        sorted.sort((a, b) => {
            const va = valueFor(a);
            const vb = valueFor(b);
            if (typeof va === 'string' || typeof vb === 'string') {
                return String(va).localeCompare(String(vb)) * dirMult;
            }
            return (va - vb) * dirMult;
        });
        return sorted;
    }, [bets, aflPropType, selectedGame, searchTerm, bookmakerFilter, aflSeasonView, sortConfig, filters]);

    const totalCount = Array.isArray(bets) ? bets.length : 0;
    const matchedCount = rows.length;
    const impliedOddsLabel = `Implied Odds (${AFL_SEASON_SHORT_LABEL[aflSeasonView] || aflSeasonView})`;

    // Layout is two stacked regions (static header + scrollable body) rather than
    // a single <table>. This avoids `position: sticky` quirks on table cells and
    // gives us reliable header-pinning on vertical scroll across all browsers.
    // Horizontal scroll on the body is mirrored onto the header via scrollLeft.
    const headerScrollRef = useRef(null);
    const bodyScrollRef = useRef(null);
    const handleBodyScroll = useCallback((e) => {
        if (headerScrollRef.current) {
            headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
        }
    }, []);

    // Measure the body's vertical scrollbar width so the header reserves matching
    // padding on its right edge — keeps the right-most cell aligned between the
    // two regions even though only the body actually has a scrollbar.
    const [scrollbarPad, setScrollbarPad] = useState(0);
    useEffect(() => {
        const el = bodyScrollRef.current;
        if (!el) return;
        const pad = el.offsetWidth - el.clientWidth;
        if (pad !== scrollbarPad) setScrollbarPad(pad);
    });

    const totalWidth =
        COL_WIDTHS.checkbox + COL_WIDTHS.edge + COL_WIDTHS.implied + COL_WIDTHS.bestBook +
        COL_WIDTHS.player + COL_WIDTHS.market + COL_WIDTHS.avgPrice + COL_WIDTHS.impliedOdds +
        COL_WIDTHS.bookmaker * allBookmakers.length;

    return (
        <div className="flex flex-col h-full bg-slate-900/40 border border-slate-700/40 rounded-lg overflow-hidden">
            <Header
                dataSource={dataSource}
                setDataSource={setDataSource}
                aflPropType={aflPropType}
                setAflPropType={setAflPropType}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                aflSeasonView={aflSeasonView}
                setAflSeasonView={setAflSeasonView}
                selectedGame={selectedGame}
                setSelectedGame={setSelectedGame}
                availableGames={availableGames}
                countdown={countdown}
                onRefresh={onRefresh}
                isLoading={isLoading}
                matchedCount={matchedCount}
                totalCount={totalCount}
                onOpenFilters={() => setShowFiltersModal(true)}
                activeFilterCount={activeFilterCount}
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
            />

            {error ? (
                <ErrorState error={error} onRetry={onRefresh} />
            ) : (
                <>
                {/* Desktop region — wide divs with sticky pillar, hidden on mobile. */}
                <div className="hidden md:flex md:flex-col md:flex-1 md:min-h-0">
                    {/* HEADER region — never scrolls vertically. `overflow-x-hidden`
                        creates a horizontal scroll container so the four left cells'
                        `position: sticky` engages; we control scrollLeft manually so
                        it mirrors the body's horizontal scroll position. */}
                    <div
                        ref={headerScrollRef}
                        className="flex-shrink-0 overflow-x-hidden bg-slate-800 backdrop-blur-md"
                        style={{ paddingRight: scrollbarPad }}
                    >
                        <div className="flex" style={{ width: '100%', minWidth: totalWidth }}>
                            {/* Checkbox header */}
                            <div
                                className="sticky left-0 z-10 bg-slate-800 flex items-center justify-center px-2 py-3 flex-shrink-0 border-b border-slate-700/50"
                                style={{ width: COL_WIDTHS.checkbox, minWidth: COL_WIDTHS.checkbox, maxWidth: COL_WIDTHS.checkbox }}
                            >
                                <span className="text-[10px] font-mono font-semibold uppercase tracking-widest text-slate-500" aria-hidden="true">+</span>
                            </div>
                            <SortableHeader
                                sortKey="edge" label="Edge%" sortConfig={sortConfig} onSort={handleSort} accent="emerald"
                                sticky left={STICKY_LEFTS.edge} width={COL_WIDTHS.edge}
                            />
                            <SortableHeader
                                sortKey="impliedEv" label="Implied %" sortConfig={sortConfig} onSort={handleSort} accent="emerald"
                                sticky left={STICKY_LEFTS.implied} width={COL_WIDTHS.implied}
                            />
                            <SortableHeader
                                sortKey="bestOdds" label="Best Book" sortConfig={sortConfig} onSort={handleSort} accent="emerald"
                                sticky left={STICKY_LEFTS.bestBook} width={COL_WIDTHS.bestBook}
                            />
                            <SortableHeader
                                sortKey="player" label="Player" sortConfig={sortConfig} onSort={handleSort}
                                sticky left={STICKY_LEFTS.player} width={COL_WIDTHS.player}
                            />
                            <SortableHeader
                                sortKey="market" label="Market" sortConfig={sortConfig} onSort={handleSort}
                                sticky left={STICKY_LEFTS.market} width={COL_WIDTHS.market} isLast
                            />
                            <SortableHeader sortKey="avgOdds" label="Avg Price" sortConfig={sortConfig} onSort={handleSort} width={COL_WIDTHS.avgPrice} />
                            <SortableHeader sortKey="impliedOdds" label={impliedOddsLabel} sortConfig={sortConfig} onSort={handleSort} width={COL_WIDTHS.impliedOdds} />
                            {allBookmakers.map((bk) => (
                                <div
                                    key={bk}
                                    className="flex items-center justify-center px-0.5 py-3 bg-slate-800 border-b border-slate-700/50"
                                    style={{ flexGrow: 1, flexShrink: 0, flexBasis: COL_WIDTHS.bookmaker, minWidth: COL_WIDTHS.bookmaker }}
                                    title={BOOKMAKER_DISPLAY_NAMES[bk] || bk}
                                >
                                    <BookmakerLogo bookmaker={bk} size={18} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* BODY region — scrolls both vertically and horizontally.
                        `scrollbarGutter: stable` reserves space for the vertical
                        scrollbar so the header and body columns stay aligned. */}
                    <div
                        ref={bodyScrollRef}
                        onScroll={handleBodyScroll}
                        className="overflow-auto flex-1 min-h-0 styled-scrollbar"
                        style={{ scrollbarGutter: 'stable' }}
                    >
                        <div style={{ width: '100%', minWidth: totalWidth }}>
                            {rows.length === 0 ? (
                                <div className="px-4 py-16 text-center text-xs font-mono text-slate-500">
                                    {isLoading ? 'Loading value bets…' : 'No value bets match the current filters.'}
                                </div>
                            ) : (
                                rows.map((bet) => {
                                    const key = getLegKey(bet);
                                    const opp = bet.valueOpportunities?.[0];
                                    if (!opp) return null;

                                    const bkOddsMap = {};
                                    bet.bookmakerOdds?.forEach((bo) => { bkOddsMap[bo.bookmaker] = bo; });

                                    const bestBk = opp.bookmaker;
                                    const selected = selectedLegKeys.has(key);

                                    const implied = getImpliedFields(opp, aflSeasonView, opp?.odds);
                                    const impliedValid = isImpliedValid({ impliedOdds: implied.impliedOdds });

                                    const rowBgClass = selected
                                        ? 'bg-emerald-500/[0.08]'
                                        : 'hover:bg-slate-800/30';

                                    return (
                                        <div
                                            key={key}
                                            onClick={() => onToggleLeg(bet)}
                                            className={`group flex cursor-pointer transition-colors border-b border-slate-800/40 ${rowBgClass}`}
                                        >
                                            {/* Checkbox (sticky) */}
                                            <div
                                                className={`flex items-center justify-center px-2 py-2.5 flex-shrink-0 ${stickyCellClasses(selected)}`}
                                                style={{ left: STICKY_LEFTS.checkbox, width: COL_WIDTHS.checkbox, minWidth: COL_WIDTHS.checkbox, maxWidth: COL_WIDTHS.checkbox }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => onToggleLeg(bet)}
                                                    aria-pressed={selected}
                                                    aria-label={selected ? 'Remove leg' : 'Add leg'}
                                                    className={`w-5 h-5 inline-flex items-center justify-center rounded border transition-all ${
                                                        selected
                                                            ? 'bg-emerald-500/30 border-emerald-400/60'
                                                            : 'bg-slate-900 border-slate-600 hover:border-emerald-500/40 hover:bg-slate-800'
                                                    }`}
                                                >
                                                    {selected && (
                                                        <svg className="w-3 h-3 text-emerald-300" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                            {/* Edge% (sticky) */}
                                            <div
                                                className={`flex items-center px-3 py-2.5 flex-shrink-0 whitespace-nowrap ${stickyCellClasses(selected)}`}
                                                style={{ left: STICKY_LEFTS.edge, width: COL_WIDTHS.edge, minWidth: COL_WIDTHS.edge, maxWidth: COL_WIDTHS.edge }}
                                            >
                                                <MetricBadge
                                                    value={opp.impliedEdgePercentage ?? null}
                                                    formatted={opp.impliedEdgePercentage != null ? `+${opp.impliedEdgePercentage.toFixed(1)}%` : null}
                                                />
                                            </div>
                                            {/* Implied EV (sticky) */}
                                            <div
                                                className={`flex items-center justify-center px-3 py-2.5 flex-shrink-0 whitespace-nowrap ${stickyCellClasses(selected)}`}
                                                style={{ left: STICKY_LEFTS.implied, width: COL_WIDTHS.implied, minWidth: COL_WIDTHS.implied, maxWidth: COL_WIDTHS.implied }}
                                            >
                                                {impliedValid && implied.impliedEv != null ? (
                                                    <MetricBadge
                                                        value={implied.impliedEv}
                                                        formatted={`${implied.impliedEv >= 0 ? '+' : ''}${Number(implied.impliedEv).toFixed(1)}%`}
                                                    />
                                                ) : <span className="text-[13px] text-slate-700">—</span>}
                                            </div>
                                            {/* Best Book (sticky) */}
                                            <div
                                                className={`flex items-center px-3 py-2.5 flex-shrink-0 whitespace-nowrap ${stickyCellClasses(selected)}`}
                                                style={{ left: STICKY_LEFTS.bestBook, width: COL_WIDTHS.bestBook, minWidth: COL_WIDTHS.bestBook, maxWidth: COL_WIDTHS.bestBook }}
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <BookmakerLogo bookmaker={bestBk} size={18} />
                                                    <span className="text-[13px] font-mono font-bold tabular-nums text-white">
                                                        {formatOdds(opp.odds)}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Player (sticky) */}
                                            <div
                                                className={`flex flex-col justify-center px-3 py-2.5 flex-shrink-0 min-w-0 ${stickyCellClasses(selected)}`}
                                                style={{ left: STICKY_LEFTS.player, width: COL_WIDTHS.player, minWidth: COL_WIDTHS.player, maxWidth: COL_WIDTHS.player }}
                                            >
                                                <span className="flex items-center gap-1.5 min-w-0">
                                                    {canUsePropTool && onOpenPropTool && (
                                                        <PropToolButton player={bet.player} onClick={() => onOpenPropTool(bet)} />
                                                    )}
                                                    <span className="text-xs font-semibold text-slate-300 truncate" title={bet.player}>
                                                        {bet.player}
                                                    </span>
                                                </span>
                                                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider truncate">
                                                    {shortenMatchName(bet.match, 'afl')}
                                                    <span className="text-slate-700"> · </span>
                                                    <span className="text-slate-600">{formatStartTime(bet.startTime)}</span>
                                                </span>
                                            </div>
                                            {/* Market (sticky, last in pillar) */}
                                            <div
                                                className={`flex items-center px-3 py-2.5 text-xs font-mono text-slate-400 truncate flex-shrink-0 ${stickyCellClasses(selected, true)}`}
                                                style={{ left: STICKY_LEFTS.market, width: COL_WIDTHS.market, minWidth: COL_WIDTHS.market, maxWidth: COL_WIDTHS.market }}
                                                title={bet.market}
                                            >
                                                <span className="truncate">{bet.market}</span>
                                            </div>
                                            {/* Avg Price */}
                                            <div
                                                className="flex items-center px-3 py-2.5 flex-shrink-0 whitespace-nowrap"
                                                style={{ width: COL_WIDTHS.avgPrice, minWidth: COL_WIDTHS.avgPrice, maxWidth: COL_WIDTHS.avgPrice }}
                                            >
                                                <span className="text-[13px] font-mono font-medium tabular-nums text-slate-400">
                                                    {formatOdds(opp.avgMarketOdds)}
                                                </span>
                                            </div>
                                            {/* Implied Odds */}
                                            <div
                                                className="flex flex-col items-center justify-center px-3 py-2.5 flex-shrink-0 whitespace-nowrap"
                                                style={{ width: COL_WIDTHS.impliedOdds, minWidth: COL_WIDTHS.impliedOdds, maxWidth: COL_WIDTHS.impliedOdds }}
                                            >
                                                {impliedValid ? (
                                                    <>
                                                        <span className="text-[13px] font-mono font-medium tabular-nums text-slate-300">
                                                            {formatOdds(implied.impliedOdds)}
                                                        </span>
                                                        {implied.hitRatePercentage != null && (
                                                            <span className="text-[10px] font-mono text-slate-500 leading-tight tabular-nums mt-0.5">
                                                                {implied.lineHit}/{implied.totalGames} ({parseFloat(Number(implied.hitRatePercentage).toFixed(2))}%)
                                                            </span>
                                                        )}
                                                    </>
                                                ) : <span className="text-[13px] text-slate-700">-</span>}
                                            </div>
                                            {/* Per-bookmaker cells */}
                                            {allBookmakers.map((bk) => {
                                                const bo = bkOddsMap[bk];
                                                const isBest = bk === bestBk;
                                                return (
                                                    <div
                                                        key={bk}
                                                        className="flex items-center justify-center px-0.5 py-2.5"
                                                        style={{ flexGrow: 1, flexShrink: 0, flexBasis: COL_WIDTHS.bookmaker, minWidth: COL_WIDTHS.bookmaker }}
                                                    >
                                                        {bo ? (
                                                            <span className="inline-flex items-center justify-center gap-1">
                                                                <span
                                                                    className={`text-[13px] font-mono tabular-nums ${
                                                                        isBest
                                                                            ? 'text-emerald-400 font-semibold'
                                                                            : 'text-slate-400'
                                                                    }`}
                                                                >
                                                                    {formatOdds(bo.odds)}
                                                                </span>
                                                                {bo.isDerived && <DerivedBadge />}
                                                            </span>
                                                        ) : (
                                                            <span className="text-[13px] text-slate-700 font-mono">-</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile region — card list. Hidden on desktop. Reuses the same
                    `rows` data + filter pipeline as the desktop table, but lays
                    each row out as a touch-friendly card. */}
                <div className="md:hidden flex-1 min-h-0 overflow-y-auto styled-scrollbar p-2 space-y-2">
                    {rows.length === 0 ? (
                        <div className="px-4 py-16 text-center text-xs font-mono text-slate-500">
                            {isLoading ? 'Loading value bets…' : 'No value bets match the current filters.'}
                        </div>
                    ) : (
                        rows.map((bet) => {
                            const key = getLegKey(bet);
                            const opp = bet.valueOpportunities?.[0];
                            if (!opp) return null;
                            const bkOddsMap = {};
                            bet.bookmakerOdds?.forEach((bo) => { bkOddsMap[bo.bookmaker] = bo; });
                            const bestBk = opp.bookmaker;
                            const selected = selectedLegKeys.has(key);
                            const impliedFields = getImpliedFields(opp, aflSeasonView, opp?.odds);
                            const impliedValid = isImpliedValid({ impliedOdds: impliedFields.impliedOdds });
                            const seasonShort = AFL_SEASON_SHORT_LABEL[aflSeasonView] || aflSeasonView;
                            return (
                                <MobileLegCard
                                    key={key}
                                    bet={bet}
                                    selected={selected}
                                    bestBk={bestBk}
                                    impliedFields={impliedFields}
                                    impliedValid={impliedValid}
                                    seasonShort={seasonShort}
                                    bkOddsMap={bkOddsMap}
                                    allBookmakers={allBookmakers}
                                    onToggle={onToggleLeg}
                                    onOpenPropTool={canUsePropTool ? onOpenPropTool : undefined}
                                />
                            );
                        })
                    )}
                </div>
                </>
            )}

            {/* Footer */}
            {!error && (
                <div className="px-3 py-2 border-t border-slate-700/40 flex items-center justify-between">
                    <span className="text-[10px] font-mono text-slate-600">
                        {lastUpdated ? `Updated ${new Date(lastUpdated).toLocaleTimeString()}` : '—'}
                    </span>
                    <span className="text-[10px] font-mono text-slate-600">
                        Tap a row to add to your multi
                    </span>
                </div>
            )}

            {showFiltersModal && filters && (
                <MultiBuilderFiltersModal
                    filters={filters}
                    onApply={onApplyFilters}
                    onClose={() => setShowFiltersModal(false)}
                    bookmakers={allBookmakers}
                    bookmakerLogos={bookmakerLogos}
                    bookmakerNames={bookmakerNames || BOOKMAKER_DISPLAY_NAMES}
                    maxBookmakers={Math.max(2, allBookmakers.length)}
                />
            )}
        </div>
    );
}

export default MultiBuilderTable;
